import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, ButtonBase, Skeleton, Typography } from "@mui/material";
import { ChevronLeft } from "@mui/icons-material";
import * as Sentry from "@sentry/react";
import { Button, FinancieraLogo, PageHeader } from "@components/ui_v2";
import logoLendia from "@assets/img/lendia/Logo.svg";
import { RootState, useAppDispatch } from "@store/store";
import { setModalErrorVisible } from "@store/slices/appSlice";
import { setSelectedOffer } from "@store/slices/marketPlaceSlice";
import { useLazyGetOffersQuery } from "@api/marketPlaceApis";
import {
  useLazyGetConditionsByIdQuery,
  usePostValidateNextStageMutation,
  usePutConditionMutation,
} from "@api/applications";
import { ApiError, LoanCondiciones } from "@interfaces/response";
import { PayloadCondition } from "@interfaces/request";
import { PATHS_EXPEDIENTE_V2, PATHS_TOFU_V2, STATUS_CATALOG } from "@helpers/constantes";
import { DialogConfirmarHandoff } from "@pages/producto/credito_simple/tofu_v2/DialogConfirmarHandoff";
import { toVista } from "@helpers/ofertasHelpers";
import { OfertaVista } from "@interfaces/ofertas";

/** Status al que avanza la solicitud al continuar (mismo código para PM y PFAE en el legacy). */
const NEXT_STATUS = STATUS_CATALOG.check_list.code;

const PATH_PREOFERTAS = PATHS_TOFU_V2.seleccionandoOferta.path;
const PATH_CHECK_LIST = PATHS_EXPEDIENTE_V2.checkList.path;

/** Tonos del diseño (`S_HandoffFinanciera`) sin equivalente en la paleta del theme. */
const CARD_BORDER = "#E7EEF4";
const INNER_RULE = "#EEF3F8";
const STAT_LABEL = "#89A0B8";
const NOTA_TITLE = "#3B6FB0";
const NOTA_TEXT = "#5C6B7C";
const MUTED = "#7E90A4";

/**
 * Oferta seleccionada (`S_HandoffFinanciera` del diseño): resumen completo de la preoferta
 * elegida antes de mandarla a formalización.
 *
 * Misma lógica que `marketplace/OfertaSeleccionada.tsx`: recupera la oferta aceptada de la
 * solicitud y su condición activa, y al continuar actualiza la condición con los montos de la
 * oferta y avanza el status al expediente.
 */
export const OfertaSeleccionada = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const application = useSelector((state: RootState) => state.register.application);
  const accountUser = useSelector((state: RootState) => state.register.account_user);

  const [getOffersMarket] = useLazyGetOffersQuery();
  const [getConditions] = useLazyGetConditionsByIdQuery();
  const [putCondition] = usePutConditionMutation();
  const [triggerPostNextStage] = usePostValidateNextStageMutation();

  const [isLoading, setIsLoading] = useState(true);
  const [oferta, setOferta] = useState<OfertaVista | null>(null);
  const [condition, setCondition] = useState<LoanCondiciones | null>(null);
  const [confirmando, setConfirmando] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        setIsLoading(true);
        // Mismo orden que el legacy: primero las ofertas, luego las condiciones.
        const responseOffers = await getOffersMarket({ id_sol: application.id }).unwrap();
        const responseConditions = await getConditions(application.id).unwrap();

        setCondition(responseConditions.find((item) => item.active) || null);

        // La oferta de esta pantalla es la que el usuario ya aceptó en el marketplace.
        const aceptada = responseOffers.find((item) => item.accept);
        if (aceptada) {
          dispatch(setSelectedOffer(aceptada));
          setOferta(toVista(aceptada));
        }
      } catch (error) {
        Sentry.captureException(error, { extra: { component: "OfertaSeleccionada-initial_fetch" } });
        dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
      } finally {
        setIsLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Guarda los montos de la oferta en la condición de la solicitud y avanza al expediente. */
  const continuar = async () => {
    if (!oferta) return;

    try {
      setConfirmando(false);
      setIsLoading(true);

      // El legacy manda `String(condition?.id)`, que sin condición activa termina pegándole a
      // `.../condiciones/undefined`; aquí se corta antes con un error explícito.
      if (!condition) {
        throw new Error("La solicitud no tiene una condición activa");
      }

      const detalles = oferta.offer.details;
      const monto = detalles?.maxAuthorizedAmount || 0;
      const plazoMax = detalles?.maxAuthorizedPeriod || 0;

      const data: PayloadCondition = {
        loan_application: application.id,
        loan_amount: monto,
        requested_term: plazoMax,
        interest_rate: detalles?.interestRate || 0,
        approved_amount: monto,
        approved_max_term: plazoMax,
        requested_amount: monto,
        selected_amount: monto,
        selected_term: plazoMax,
        approved_min_term: detalles?.minAuthorizedPeriod || 0,
      };

      await putCondition({ body: data, id: condition.id }).unwrap();

      await triggerPostNextStage({
        applicationId: application.id,
        statusCode: NEXT_STATUS,
        username: `TOFU - ${accountUser.email}`,
      }).unwrap();

      // TODO: el legacy manda a los PM a `check_list` y a los PFAE a
      // `informacion_documentos_pfae` (mismo status `CSBEX`, distinta URL). El wizard nuevo
      // solo tiene la lista de revisión, así que ambos regímenes caen ahí.
      navigate(PATH_CHECK_LIST);
    } catch (error) {
      Sentry.captureException(error, { extra: { component: "OfertaSeleccionada-continuar" } });
      dispatch(setModalErrorVisible({ open: true, error: error as ApiError }));
    } finally {
      setIsLoading(false);
    }
  };

  const financiera = oferta?.card.financiera || "";
  const esDigital = Boolean(oferta?.card.digital);

  return (
    <Box>
      <PageHeader
        eyebrow="PRE-APROBACIÓN · SIGUIENTE PASO"
        title={`Tu oferta continúa con ${financiera}`}
        subtitle={
          esDigital ? (
            <>
              Esta oferta se completará a través del flujo digital de <strong>{financiera}</strong>, operado en la
              plataforma de Lendia – Pymio.
              <br />
              Al continuar, <strong>pasarás al entorno digital de {financiera}</strong> para concluir tu oferta ahí, con
              el mismo equipo y acompañamiento que te ha atendido hasta ahora.
            </>
          ) : (
            <>
              Revisa los detalles completos de tu oferta con <strong>{financiera}</strong>.
              <br />
              Al continuar, <strong>armarás tu Expediente digital en Pymio</strong> y de ahí pasarás a la formalización
              con la financiera.
            </>
          )
        }
        fullWidthSubtitle
      />

      {isLoading && !oferta && (
        <Skeleton variant="rectangular" animation="wave" sx={{ height: 380, borderRadius: "20px" }} />
      )}

      {!isLoading && !oferta && (
        <Typography sx={{ textAlign: "center", p: "48px 20px", fontSize: 15, color: MUTED }}>
          No se encontró una oferta seleccionada.
        </Typography>
      )}

      {oferta && (
        <Box
          sx={{
            bgcolor: "background.paper",
            border: `1px solid ${CARD_BORDER}`,
            borderRadius: "20px",
            p: "26px 30px",
            boxShadow: "0 8px 28px rgba(6, 44, 78, 0.08)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              flexWrap: "wrap",
              pb: "18px",
              borderBottom: `1px solid ${INNER_RULE}`,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: "4px" }}>
              <FinancieraLogo logo={oferta.card.logo} financiera={financiera} maxHeight={34} maxWidth={150} />
              {esDigital && (
                <Typography
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: "4px",
                    fontWeight: 500,
                    fontSize: 9.5,
                    color: NOTA_TITLE,
                  }}
                >
                  Powered by <Box component="img" src={logoLendia} alt="Lendia" sx={{ height: 10, display: "block" }} />
                </Typography>
              )}
            </Box>

            <Box sx={{ textAlign: "right" }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: 12,
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  color: "primary.accent",
                }}
              >
                {oferta.card.producto}
              </Typography>
              <Typography sx={{ fontWeight: 500, fontSize: 12, color: MUTED, mt: "3px" }}>
                {esDigital ? "Proceso 100% digital" : "Formalización con la financiera"}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "18px 14px", py: "20px" }}>
            {oferta.card.stats.map((stat) => (
              <Box key={stat.label} sx={{ textAlign: "center" }}>
                <Typography sx={{ fontSize: 11, color: STAT_LABEL, mb: "4px" }}>{stat.label}</Typography>
                <Typography sx={{ fontWeight: 600, fontSize: 14, color: "primary.dark" }}>{stat.value}</Typography>
              </Box>
            ))}
          </Box>

          <Box sx={{ pt: "16px", borderTop: `1px solid ${INNER_RULE}` }}>
            <Typography sx={{ fontWeight: 600, fontSize: 12.5, color: NOTA_TITLE, mb: "8px" }}>
              {oferta.card.notaTitulo}
            </Typography>
            <Box
              component="ul"
              sx={{ listStyle: "none", m: 0, p: 0, display: "flex", flexDirection: "column", gap: "6px" }}
            >
              {oferta.card.notas.map((nota) => (
                <Box
                  component="li"
                  key={nota}
                  sx={{ display: "flex", gap: "8px", fontSize: 12.5, lineHeight: 1.45, color: NOTA_TEXT }}
                >
                  <Box component="span" sx={{ color: MUTED }}>
                    •
                  </Box>
                  <span>{nota}</span>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: "26px" }}>
        <ButtonBase
          onClick={() => navigate(PATH_PREOFERTAS)}
          disabled={isLoading}
          data-testid="oferta-seleccionada-back-button"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            p: "10px 4px",
            fontWeight: 600,
            fontSize: 13.5,
            color: MUTED,
            transition: "color .15s",
            "&:hover": { color: "primary.dark" },
          }}
        >
          <ChevronLeft sx={{ fontSize: 16 }} /> Regresar a mis preofertas
        </ButtonBase>

        <Button
          tone="accent"
          withArrow
          disabled={isLoading || !oferta}
          onClick={() => setConfirmando(true)}
          sx={{ p: "13px 28px", fontSize: 15, borderRadius: "12px", boxShadow: "0 8px 20px rgba(255, 157, 20, 0.32)" }}
          data-testid="oferta-seleccionada-continue-button"
        >
          Continuar
        </Button>
      </Box>

      {confirmando && oferta && (
        <DialogConfirmarHandoff
          oferta={oferta.card}
          disabled={isLoading}
          onConfirm={continuar}
          onClose={() => setConfirmando(false)}
        />
      )}
    </Box>
  );
};
